import { DispositionScheme } from '@/modules/dispositiveProvisions/dispositiveProvisions.types';
import { DispositiveProvisionDispositionKind } from '@/types/schema';

import {
  DispositiveProvisionsForm_DispositionScenario,
  DispositiveProvisionsFormRecipient,
} from './DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import {
  getDispositionKind,
  getRecipientDetails,
} from './DispositiveProvisionsForm/DispositiveProvisionsForm.utils';
import { DispositiveProvisions_DispositiveProvisionFragment } from './graphql/DispositiveProvisions.fragments.generated';

/**
 * @description Returns a recipient for a given provision.
 */
export function mapProvisionToRecipient(
  provision: DispositiveProvisions_DispositiveProvisionFragment
): DispositiveProvisionsFormRecipient {
  const { recipientId, recipientKind } = getRecipientDetails(provision);
  const { dispositionKind_ButtonGroup, dispositionKind_Select } =
    getDispositionKind(provision);

  if (!recipientId) {
    throw new Error('Could not find recipient id');
  }

  // Detect asset-level DP: if any asset edges are populated, this is an asset DP
  const assetGroupIds = provision.assetGroups?.map((a) => a.id) ?? [];
  const assetStaticIds = provision.assetStatics?.map((a) => a.id) ?? [];
  const insurancePolicyIds =
    provision.insurancePolicies?.map((a) => a.id) ?? [];
  const ownershipStakeIds = provision.ownershipStakes?.map((a) => a.id) ?? [];
  const isAssetDp =
    assetGroupIds.length > 0 ||
    assetStaticIds.length > 0 ||
    insurancePolicyIds.length > 0 ||
    ownershipStakeIds.length > 0;

  return {
    recipientId,
    recipientKind,
    dispositionKind_ButtonGroup: isAssetDp
      ? 'asset'
      : dispositionKind_ButtonGroup,
    dispositionKind_Select,
    dispositionAmount: provision.dispositionAmount ?? null,
    // For asset DPs, dispositionPercentage from the server maps to dispositionAssetPercentage
    dispositionPercentage: isAssetDp
      ? null
      : (provision.dispositionPercentage ?? null),
    dispositionFormulaicPercentage:
      provision.dispositionFormulaicPercentage ?? null,
    notes: provision.notes ?? '',
    transferTaxKind: provision.transferTaxKind,
    bearsTax: provision.bearsTax ?? false,
    _dispositiveProvisionId: provision.id,
    dispositionLimit: provision.dispositionLimit ?? null,
    growByInflation: provision.growByInflation ?? false,
    transferMethod: provision.transferMethod,
    assetGroupIds,
    assetStaticIds,
    insurancePolicyIds,
    ownershipStakeIds,
    dispositionAssetPercentage: isAssetDp
      ? (provision.dispositionPercentage ?? null)
      : null,
  };
}

/**
 * A disposition is eligible for grow-by-inflation if it is a dollar amount
 * or a percentage disposition. Formulaic dispositions are not eligible because
 * they always grow by inflation (forced on separately in the UI).
 */
export function isEligibleForInflation(disposition: {
  dispositionKind_ButtonGroup:
    | DispositiveProvisionDispositionKind.Percentage
    | DispositiveProvisionDispositionKind.Amount
    | 'other'
    | 'asset';
}): boolean {
  return (
    disposition.dispositionKind_ButtonGroup ===
      DispositiveProvisionDispositionKind.Amount ||
    disposition.dispositionKind_ButtonGroup ===
      DispositiveProvisionDispositionKind.Percentage
  );
}

export const sortDispositionOrder = (
  a: DispositiveProvisions_DispositiveProvisionFragment,
  b: DispositiveProvisions_DispositiveProvisionFragment
) => (a.dispositionOrder ?? 0) - (b.dispositionOrder ?? 0);

/**
 * @description Returns a map where each disposition scheme is mapped to null,
 * to be used as the "empty" state input when converting the V1 and V2 formValues
 * into the mutation input for form submission.
 */
export function getEmptyDPMap(): Record<
  DispositionScheme,
  DispositiveProvisionsForm_DispositionScenario | null
> {
  return {
    [DispositionScheme.UPON_FIRST_DEATH]: null,
    [DispositionScheme.UPON_SECOND_DEATH]: null,
    [DispositionScheme.NONE]: null,
  } as const;
}
